/**
 * Trailing-window player heatmap (founder ask, 2026-08-27).
 *
 * Where has a team been over the last couple of minutes. It reads the frames
 * the canvas controller is ALREADY holding — `canvasState.frames` is a rolling
 * buffer of `maxBufferSize` (3600 frames, two minutes at 30fps) — so this
 * costs no fetch, no parquet read and no endpoint. That buffer is also the
 * hard ceiling on the window: ask for more than it holds and you get what it
 * has, which is why WINDOW_FRAMES is not configurable past it.
 *
 * The grid, the colour ramp's shape and the blur are deliberately the same
 * family as spatialControl.ts so the two layers read as one system rather
 * than two visualizations that happen to share a pitch.
 */
import type { TrackingFrame } from './trackingLoader'

/** One cell per metre, matching spatialControl's grid exactly. */
export const GRID_X = 106
export const GRID_Y = 69

/** 2 minutes at 30fps — the whole buffer. */
export const WINDOW_FRAMES = 3600

/** Pitch half-extents in centimetres; tracking coords are cm from the centre
 *  spot, y up-positive (see CanvasController.convertToCanvasCoords). */
const HALF_LEN_CM = 105 * 100 / 2
const HALF_WID_CM = 68 * 100 / 2

export interface HeatOptions {
  /** 0 or 1 to isolate a team; null accumulates both. */
  team?: number | null
  /** How many frames back from `endIndex` to include. */
  windowFrames?: number
  /**
   * Restrict to these "team_jersey" keys. Empty/undefined = the whole team.
   * Set when players are focused, so the map answers "where has HE been"
   * rather than "where has the team been".
   */
  only?: string[]
  /**
   * Recency half-life in frames. A position this old counts half as much as
   * one at the playhead; twice as old, a quarter. Without it every frame in
   * the window weighed the same, so the map only ever grew — it never cooled
   * (founder ask, 2026-08-27: "cool off faster").
   */
  halfLifeFrames?: number
}

/** ~13s at 30fps. Old enough to show a phase of play, short enough that the
 *  map visibly follows the game rather than averaging the whole window. */
export const HALF_LIFE_FRAMES = 400

/**
 * How hard to lift low-occupancy cells before colouring — "the field should
 * wear out faster" (founder, 2026-08-27).
 *
 * Occupancy against the busiest cell is a brutally skewed distribution: a
 * keeper standing still dominates, and everything a moving player did reads
 * as near-zero. A square root (0.5) was still too shy — at 0.35 a cell that
 * has seen a third of the peak traffic already shows as worn, which is what
 * makes the pitch look played-on rather than spotted.
 *
 * Lower = wears faster. Raise toward 0.5 to calm it down.
 */
const WEAR_EXPONENT = 0.35

/** Same idea for opacity: faint cells need to be visible, not merely present. */
const ALPHA_EXPONENT = 0.45

/**
 * Bin one frame's players into grid cell indices under the given filters.
 * Shared by accumulate() and IncrementalHeat so the two routes cannot
 * disagree about what a frame contributes.
 */
function frameCells(
  players: TrackingFrame['players'] | undefined,
  team: number | null,
  wanted: Set<string> | null,
): number[] {
  const cells: number[] = []
  if (!players) return cells
  for (const p of players) {
    if (wanted) {
      // Focus wins over team: an explicit selection is a stronger statement
      // than "this side", and mixing them would silently drop a focused
      // opponent.
      if (p.jersey == null || !wanted.has(`${p.team}_${p.jersey}`)) continue
    } else if (team != null && p.team !== team) continue
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
    // cm from centre -> grid cell. y is negated because pitch y is
    // up-positive and row 0 of the grid is the top of the image.
    const gx = Math.floor(((p.x + HALF_LEN_CM) / (HALF_LEN_CM * 2)) * GRID_X)
    const gy = Math.floor(((HALF_WID_CM - p.y) / (HALF_WID_CM * 2)) * GRID_Y)
    // A player who steps off the pitch is clamped to the edge rather than
    // dropped: they were still there, and dropping them puts a false cold
    // band along the touchline.
    const cx = gx < 0 ? 0 : gx >= GRID_X ? GRID_X - 1 : gx
    const cy = gy < 0 ? 0 : gy >= GRID_Y ? GRID_Y - 1 : gy
    cells.push(cx * GRID_Y + cy)
  }
  return cells
}

/**
 * Bin player positions into a GRID_X * GRID_Y occupancy grid.
 *
 * Pure and exported for its own sake: the binning is the part that can be
 * silently wrong (a flipped axis still renders a plausible-looking blob), so
 * it is unit-tested rather than eyeballed on a pitch.
 *
 * Returns counts, not a normalized field — the caller decides the ramp.
 */
export function accumulate(
  frames: TrackingFrame[],
  endIndex: number,
  { team = null, windowFrames = WINDOW_FRAMES, only,
    halfLifeFrames = HALF_LIFE_FRAMES }: HeatOptions = {},
): Float32Array {
  const grid = new Float32Array(GRID_X * GRID_Y)
  if (!frames.length) return grid

  const end = Math.min(endIndex, frames.length - 1)
  const start = Math.max(0, end - windowFrames + 1)

  const wanted = only && only.length ? new Set(only) : null
  const decay = halfLifeFrames > 0 ? Math.LN2 / halfLifeFrames : 0
  for (let i = start; i <= end; i++) {
    // Weight by how recent the frame is, not merely whether it is in range.
    const w = decay ? Math.exp(-decay * (end - i)) : 1
    for (const c of frameCells(frames[i]?.players, team, wanted)) {
      // A Float32Array read inside its own length is always a number; the
      // checker does not model typed arrays that way, and 0 is what an
      // untouched cell holds anyway, so the fallback is the true value.
      grid[c] = (grid[c] ?? 0) + w
    }
  }
  return grid
}

/** One frame's remembered contribution: its identity (the tracking frame
 *  number, which survives the buffer rolling under us) and the grid cells it
 *  touched under the CURRENT filters. */
interface WindowEntry {
  frame: number
  cells: number[]
}

/** Incremental steps between drift-flushing full rebuilds. At one step per
 *  second of playback that is a rebuild every ~4 minutes — far below where
 *  Float64 decay/subtract drift could become visible in the Float32 output. */
export const REBUILD_EVERY = 256

/**
 * Incremental accumulation (follow-ups #19B).
 *
 * accumulate() re-bins the whole window on every recomputation: ~3600 frames
 * × ~11 players ≈ 40k position lookups per 1-second bucket. This class keeps
 * a running grid plus a ring of per-frame contributions, so advancing the
 * playhead by Δ frames costs one decay multiply over the 7,314 cells, the Δ
 * new frames binned in, and the frames that left the window subtracted out —
 * O(cells + Δ·players) instead of O(window·players).
 *
 * The exponential decay is what makes a running total possible at all: every
 * weight is exp(-λ·age), so aging the whole grid by Δ is a single multiply
 * by exp(-λ·Δ) — no stored weight ever has to be revisited. Ages are
 * positional (distance from the window's end), exactly as accumulate()
 * computes them, so the two routes agree bit-for-bit up to float error.
 *
 * Correctness never depends on the incremental path: whenever its
 * assumptions fail — filters changed, the playhead moved backward, the
 * buffer was replaced under us, duplicate or missing frame numbers, or
 * enough steps have run that float drift is worth flushing — it falls back
 * to a full rebuild, which is just accumulate()'s loop. The parity tests pin
 * the two routes to each other.
 */
export class IncrementalHeat {
  /** Float64 so repeated decay-multiply/subtract cycles do not chew through
   *  the mantissa; callers receive a Float32 copy matching accumulate(). */
  private grid = new Float64Array(GRID_X * GRID_Y)
  private window: WindowEntry[] = []
  private optsKey: string | null = null
  private steps = 0

  reset(): void {
    this.window = []
    this.optsKey = null
    this.steps = 0
  }

  update(
    frames: TrackingFrame[],
    endIndex: number,
    { team = null, windowFrames = WINDOW_FRAMES, only,
      halfLifeFrames = HALF_LIFE_FRAMES }: HeatOptions = {},
  ): Float32Array {
    const end = Math.min(endIndex, frames.length - 1)
    if (!frames.length || end < 0) {
      this.reset()
      return new Float32Array(GRID_X * GRID_Y)
    }
    const start = Math.max(0, end - windowFrames + 1)
    const wanted = only && only.length ? new Set(only) : null
    const decay = halfLifeFrames > 0 ? Math.LN2 / halfLifeFrames : 0
    const key = `${team}:${wanted ? [...wanted].join(',') : ''}:${windowFrames}:${halfLifeFrames}`

    if (
      key !== this.optsKey ||
      this.steps >= REBUILD_EVERY ||
      !this._advance(frames, start, end, decay, team, wanted)
    ) {
      this._rebuild(frames, start, end, decay, team, wanted)
      this.optsKey = key
      this.steps = 0
    } else {
      this.steps++
    }
    return Float32Array.from(this.grid)
  }

  /**
   * Try to advance the running grid from its stored window to [start..end].
   * Returns false — WITHOUT touching any state — unless the stored window is
   * a pure "drop k from the front, append m at the back" predecessor of the
   * new one. Backward seeks, replaced buffers, non-monotonic frame numbers
   * and the first call all land in the rebuild instead.
   */
  private _advance(
    frames: TrackingFrame[],
    start: number,
    end: number,
    decay: number,
    team: number | null,
    wanted: Set<string> | null,
  ): boolean {
    const stored = this.window
    const newest = stored[stored.length - 1]
    if (!newest || !Number.isFinite(newest.frame)) return false

    // Find the stored newest frame in the new window by scanning back from
    // `end`. Everything after it must be strictly newer — that is what makes
    // frame numbers usable as identity here; duplicates force a rebuild.
    let boundary = -1
    for (let i = end; i >= start; i--) {
      const fn = frames[i]?.frame
      if (fn === newest.frame) {
        boundary = i
        break
      }
      if (!(typeof fn === 'number' && fn > newest.frame)) return false
    }
    if (boundary < 0) return false // no overlap with the stored window

    const m = end - boundary // frames appended since last update
    const overlap = boundary - start + 1 // stored entries still in the window
    const k = stored.length - overlap // stored entries that fell out the front
    if (k < 0) return false // window grew backward — not an advance
    // The surviving head must be the frame the buffer now holds at `start`;
    // anything else means the buffer changed under us.
    if (stored[k]?.frame !== frames[start]?.frame) return false
    if (m === 0 && k === 0) return true // same window — the grid is current

    // Age every cell by the m frames that were appended.
    if (decay && m > 0) {
      const f = Math.exp(-decay * m)
      for (let i = 0; i < this.grid.length; i++) this.grid[i] = (this.grid[i] ?? 0) * f
    }
    // Subtract the k frames that left the window, at their post-aging weight.
    // At a 3600-frame window and the default half-life these weights are
    // ~0.002, so the subtraction is numerically gentle — no cancellation.
    for (let j = 0; j < k; j++) {
      const entry = stored[j]
      if (!entry) return false
      const w = decay ? Math.exp(-decay * (stored.length - 1 - j + m)) : 1
      for (const c of entry.cells) this.grid[c] = (this.grid[c] ?? 0) - w
    }
    if (k > 0) stored.splice(0, k)
    // Bin the m new frames in at their positional weight.
    for (let i = boundary + 1; i <= end; i++) {
      const cells = frameCells(frames[i]?.players, team, wanted)
      const w = decay ? Math.exp(-decay * (end - i)) : 1
      for (const c of cells) this.grid[c] = (this.grid[c] ?? 0) + w
      stored.push({ frame: frames[i]?.frame ?? Number.NaN, cells })
    }
    return true
  }

  /** The full O(window·players) pass — accumulate()'s loop, remembering each
   *  frame's cells so the next call can advance instead. */
  private _rebuild(
    frames: TrackingFrame[],
    start: number,
    end: number,
    decay: number,
    team: number | null,
    wanted: Set<string> | null,
  ): void {
    this.grid.fill(0)
    this.window = []
    for (let i = start; i <= end; i++) {
      const cells = frameCells(frames[i]?.players, team, wanted)
      const w = decay ? Math.exp(-decay * (end - i)) : 1
      for (const c of cells) this.grid[c] = (this.grid[c] ?? 0) + w
      this.window.push({ frame: frames[i]?.frame ?? Number.NaN, cells })
    }
  }
}

/** Transparent → warm. Alpha carries most of the signal so the pitch, its
 *  lines and the players all stay readable underneath. */
export function ramp(t: number): [number, number, number, number] {
  if (t <= 0) return [0, 0, 0, 0]
  const v = Math.min(1, t)
  // Reaches red well before the maximum (founder ask: "redder faster"). The
  // cube pulls the yellow-to-red transition down the scale, so a cell only has
  // to be moderately busy to read as hot.
  const heat = 1 - Math.pow(1 - v, 3)
  const r = 255
  const g = Math.round(225 - 205 * heat)
  const b = Math.round(90 - 90 * heat)
  return [r, g, b, Math.round(215 * Math.pow(v, ALPHA_EXPONENT))]
}

/** Same wear/alpha shape as ramp(), hue fixed to one team's color: cool
 *  cells render the color lightened toward white, hot cells the color
 *  itself. The team-split panels use it so each side's map reads in its
 *  team's color, the way the pass panels already do. */
export function tintRamp(t: number, [tr, tg, tb]: [number, number, number]): [number, number, number, number] {
  if (t <= 0) return [0, 0, 0, 0]
  const v = Math.min(1, t)
  const heat = 1 - Math.pow(1 - v, 3) // same curve as ramp(): hot early
  const lift = 0.55 * (1 - heat)
  const r = Math.round(tr + (255 - tr) * lift)
  const g = Math.round(tg + (255 - tg) * lift)
  const b = Math.round(tb + (255 - tb) * lift)
  return [r, g, b, Math.round(215 * Math.pow(v, ALPHA_EXPONENT))]
}

/** Grid -> an offscreen GRID_X x GRID_Y canvas, ready to scale onto a pitch.
 *  `tint` ([r,g,b]) swaps the fixed warm ramp for the single-hue team ramp. */
export function renderGrid(grid: Float32Array, tint?: [number, number, number]): HTMLCanvasElement | null {
  let max = 0
  for (const v of grid) if (v > max) max = v
  if (max <= 0) return null

  const img = new ImageData(GRID_X, GRID_Y)
  for (let gx = 0; gx < GRID_X; gx++) {
    for (let gy = 0; gy < GRID_Y; gy++) {
      // Not linear: occupancy is dominated by the few cells a player stood
      // still in, and a linear ramp renders as one hot dot on an otherwise
      // empty pitch. See WEAR_EXPONENT.
      const t = Math.pow((grid[gx * GRID_Y + gy] ?? 0) / max, WEAR_EXPONENT)
      const [r, g, b, a] = tint ? tintRamp(t, tint) : ramp(t)
      const idx = (gy * GRID_X + gx) * 4
      img.data[idx] = r
      img.data[idx + 1] = g
      img.data[idx + 2] = b
      img.data[idx + 3] = a
    }
  }
  const off = document.createElement('canvas')
  off.width = GRID_X
  off.height = GRID_Y
  off.getContext('2d')?.putImageData(img, 0, 0)
  return off
}

interface HeatMapOptions {
  getCanvasState: () => { ctx: CanvasRenderingContext2D | null; frames: TrackingFrame[]; currentFrameIndex: number }
  getFieldRect: () => { x: number; y: number; w: number; h: number }
}

export class HeatMapManager {
  enabled = false
  /** null = both teams. Follows the page's team picker. */
  team: number | null = null
  /** Focused players; when set, the map is theirs rather than the team's. */
  only: string[] = []
  config = { alpha: 0.75, blurRadius: 6 }

  private opts: HeatMapOptions
  private cache: { key: string; canvas: HTMLCanvasElement | null } | null = null
  /** Running grid: recomputations cost the frames that changed, not the
   *  whole window (follow-ups #19B). Falls back to a full pass on seeks. */
  private heat = new IncrementalHeat()

  constructor(opts: HeatMapOptions) {
    this.opts = opts
  }

  /** Recompute on the next draw. Call when the window or team changes. */
  invalidate(): void {
    this.cache = null
  }

  draw(): void {
    if (!this.enabled) return
    const { ctx, frames, currentFrameIndex } = this.opts.getCanvasState()
    if (!ctx || !frames.length) return

    // Re-binning 3600 frames on every animation frame would drop playback, so
    // the rendered image is cached and only rebuilt when the playhead has
    // moved enough to matter — a second of video, not a frame of it.
    const bucket = Math.floor(currentFrameIndex / 30)
    const key = `${bucket}:${this.team}:${this.only.join(',')}:${frames.length}`
    if (!this.cache || this.cache.key !== key) {
      const grid = this.heat.update(frames, currentFrameIndex,
        { team: this.team, only: this.only })
      this.cache = { key, canvas: renderGrid(grid) }
    }
    if (!this.cache.canvas) return

    const f = this.opts.getFieldRect()
    ctx.save()
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.globalAlpha = this.config.alpha
    ctx.filter = `blur(${this.config.blurRadius}px)`
    ctx.drawImage(this.cache.canvas, f.x, f.y, f.w, f.h)
    ctx.restore()
  }
}
